import { CalendarStar } from "@phosphor-icons/react";
import RankBadge, { computeRanks } from "./RankBadge";
import { fmtCurrency, fmtLakh } from "../lib/format";

function pct(v) {
  return v == null ? "—" : `${v.toFixed(1)}%`;
}

// Encouraging per-floor message. Emojis are intentional (requested copy).
function floorMessage(f) {
  const remaining = f.remaining != null ? f.remaining : (f.target - f.total_achieved);
  const p = f.pct_achieved;
  if (remaining > 0) {
    return { icon: "🚀", tone: "text-blue-700", lakh: `₹${fmtLakh(remaining)}L`, inr: fmtCurrency(remaining), tail: "more — Let's make it happen!" };
  }
  if (p != null && Math.abs(p - 100) < 0.05) {
    return { icon: "🎉", tone: "text-emerald-700", full: "100% Achieved — We made it happen!" };
  }
  return { icon: "🏆", tone: "text-amber-600", full: `${p != null ? p.toFixed(0) : "—"}% Achieved — Target exceeded!` };
}

function ProgressMessage({ mp }) {
  if (!mp) return null;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-[#BBF7D0] bg-[#F0FDF4] px-4 py-2.5 text-sm font-semibold text-[#166534]" data-testid="month-progress-message">
      <CalendarStar size={18} weight="fill" className="text-[#16A34A]" />
      {mp.days_remaining} {mp.days_remaining === 1 ? "day" : "days"} · {mp.weekends_remaining} {mp.weekends_remaining === 1 ? "weekend" : "weekends"} remaining to make it happen!
    </div>
  );
}

// mode: "staff" (₹ Lakhs, card layout) | "admin" (full ₹, detailed table)
export default function FloorTargetBoard({ mode, data }) {
  const rows = data?.floor_targets || [];
  const ranks = computeRanks(rows.map((f) => ({ name: f.floor, pct: f.pct_achieved })), ["pct"]);
  const isStaff = mode === "staff";

  if (rows.length === 0) {
    return <div className="rounded-lg border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">No floor targets configured yet.</div>;
  }

  if (isStaff) {
    return (
      <div className="space-y-3" data-testid="floortarget-board-staff">
        <ProgressMessage mp={data.month_progress} />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {rows.map((f) => {
            const msg = floorMessage(f);
            const width = Math.min(f.pct_achieved || 0, 100);
            return (
              <div key={f.floor} data-testid={`floortarget-${f.floor}`} className="rounded-lg border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="font-display text-base font-bold text-slate-900">{f.floor}</div>
                  <RankBadge rank={ranks.pct?.[f.floor]} />
                </div>
                <div className="mt-3 flex items-stretch gap-2">
                  <div className="flex-1 rounded-md bg-slate-100 px-3 py-2 text-center">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Target</div>
                    <div className="font-mono-data text-lg font-bold text-slate-700">{fmtLakh(f.target)}</div>
                  </div>
                  <div className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-center text-white">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-blue-100">Total Achieved</div>
                    <div className="font-mono-data text-lg font-bold">{fmtLakh(f.total_achieved)}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-end gap-2 border-t border-slate-100 pt-2 text-xs">
                  <span className="font-semibold uppercase tracking-wide text-[#16A34A]">Yesterday</span>
                  <span className="font-mono-data text-sm font-bold text-[#166534]">+{fmtLakh(f.yesterday)}</span>
                </div>
                <div className="mt-3">
                  <div className="mb-1 flex justify-between text-xs text-slate-500">
                    <span className="font-semibold text-slate-700">{pct(f.pct_achieved)} achieved</span>
                  </div>
                  <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${width}%` }} />
                  </div>
                </div>
                <div className={`mt-3 text-sm font-semibold ${msg.tone}`}>
                  {msg.icon} {msg.full ? msg.full : `${msg.lakh} ${msg.tail}`}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Admin — detailed full-₹ table
  return (
    <div className="space-y-3" data-testid="floortarget-board-admin">
      <ProgressMessage mp={data.month_progress} />
      <div className="thin-scroll overflow-x-auto rounded-lg border border-slate-200 bg-white">
        <table className="w-full min-w-[760px]">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/60 text-xs font-semibold uppercase tracking-wide text-slate-500">
              <th className="px-3 py-2.5 text-left">Floor</th>
              <th className="px-3 py-2.5 text-right">Target</th>
              <th className="px-3 py-2.5 text-right">Till Day Before Yest.</th>
              <th className="px-3 py-2.5 text-right">Yesterday</th>
              <th className="px-3 py-2.5 text-right">Till Date</th>
              <th className="px-3 py-2.5 text-right">% Achieved</th>
              <th className="px-3 py-2.5 text-right">Remaining</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((f) => {
              const remaining = f.remaining != null ? f.remaining : (f.target - f.total_achieved);
              const done = remaining <= 0;
              return (
                <tr key={f.floor} data-testid={`admin-floortarget-${f.floor}`} className="border-b border-slate-100">
                  <td className="px-3 py-3 text-sm font-semibold text-slate-800">{f.floor}</td>
                  <td className="px-3 py-3 text-right font-mono-data text-sm font-bold text-slate-900">{fmtCurrency(f.target)}</td>
                  <td className="px-3 py-3 text-right font-mono-data text-sm text-slate-600">{fmtCurrency(f.till_day_before_yesterday)}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right">
                    <span className={`inline-block rounded-md px-2 py-0.5 font-mono-data text-sm font-bold ${f.yesterday >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`}>
                      {f.yesterday >= 0 ? "+" : ""}{fmtCurrency(f.yesterday)}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-right font-mono-data text-sm font-bold text-slate-900">{fmtCurrency(f.total_achieved)}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono-data text-sm font-semibold text-slate-900">
                    {pct(f.pct_achieved)}<RankBadge rank={ranks.pct?.[f.floor]} />
                  </td>
                  <td className={`px-3 py-3 text-right font-mono-data text-sm font-semibold ${done ? "text-emerald-600" : "text-slate-700"}`}>
                    {done ? "Achieved" : fmtCurrency(remaining)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
